// src/components/modals/ViewProductModal.jsx
import React from 'react'
import { X, Eye, Package, MapPin, DollarSign, Truck } from 'lucide-react'
import ProductInventoryModel from '../../utils/productInventoryModel'
import ProductInventoryService from '../../services/productInventoryService'

const ViewProductModal = ({ isOpen, onClose, inventory }) => {
  if (!isOpen || !inventory) return null

  // Get stock status info
  const currentStock = Number(inventory.currentStock) || 0
  const threshold = Number(inventory.product?.threshold) || 10
  const status = ProductInventoryService.getStockStatus(currentStock, threshold)
  const statusColor = ProductInventoryModel.getStatusColor(status)
  const statusLabel = ProductInventoryModel.getStatusLabel(status)

  // Format price display
  const formatPrice = (price) => {
    return `Rs ${Number(price || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  // Format date display
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          aria-hidden="true"
          onClick={onClose}
        ></div>

        {/* Center modal */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="flex items-center space-x-3">
                  <h3 className="text-lg font-semibold text-gray-900">
                    View Product
                  </h3>

                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="bg-white rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            {/* Product Basic Info */}
            <div className="mb-6">
              <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Package className="h-12 w-12 text-blue-600 mr-4" />
                    <div>
                      <h4 className="text-xl font-semibold text-gray-900">
                        {inventory.product?.name || 'Unknown Product'}
                      </h4>
                      <p className="text-sm text-gray-600">
                        SKU: <span className="font-medium">{inventory.product?.sku || 'N/A'}</span>
                      </p>
                      <p className="text-sm text-gray-600">
                        Category: <span className="font-medium">{inventory.product?.category?.name || 'Uncategorized'}</span>
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusColor}`}>
                      {statusLabel}
                    </span>
                    <p className="text-xs text-gray-500 mt-1">
                      {inventory.isActive ? 'Active' : 'Inactive'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Stock Information */}
            <div className="mb-6">
              <h5 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <Package className="h-5 w-5 mr-2 text-gray-400" />
                Stock Information
              </h5>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500">Current Stock</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {currentStock.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    {inventory.product?.unit?.name || 'Units'} ({inventory.product?.unit?.abbreviation || 'U'})
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500">Threshold</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {threshold.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500">
                    Low stock alert level
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500">Stock Value</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {formatPrice(currentStock * (inventory.product?.cost || 0))}
                  </p>
                  <p className="text-xs text-gray-500">
                    Total inventory value
                  </p>
                </div>
              </div>
            </div>

            {/* Branch Information */}
            <div className="mb-6">
              <h5 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <MapPin className="h-5 w-5 mr-2 text-gray-400" />
                Branch Information
              </h5>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Branch Name</p>
                    <p className="text-base text-gray-900">{inventory.branch?.name || 'Unknown Branch'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Branch Type</p>
                    <p className="text-base text-gray-900 capitalize">{inventory.branch?.type || 'N/A'}</p>
                  </div>
                  <div className="sm:col-span-2">
                    <p className="text-sm font-medium text-gray-500">Address</p>
                    <p className="text-base text-gray-900">{inventory.branch?.address || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Phone</p>
                    <p className="text-base text-gray-900">{inventory.branch?.phoneNumber || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Email</p>
                    <p className="text-base text-gray-900">{inventory.branch?.email || 'N/A'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Pricing Information */}
            <div className="mb-6">
              <h5 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <DollarSign className="h-5 w-5 mr-2 text-gray-400" />
                Pricing Information
              </h5>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500">Cost Price</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatPrice(inventory.product?.cost)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500">Outlet Price</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatPrice(inventory.product?.outletPrice)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500">PickMe Price</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatPrice(inventory.product?.pickMePrice)}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-500">Uber Price</p>
                  <p className="text-lg font-bold text-gray-900">
                    {formatPrice(inventory.product?.uberPrice)}
                  </p>
                </div>
              </div>
            </div>

            {/* Product Details */}
            <div className="mb-6">
              <h5 className="text-lg font-medium text-gray-900 mb-3 flex items-center">
                <Package className="h-5 w-5 mr-2 text-gray-400" />
                Product Details
              </h5>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Product Type</p>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 mt-1">
                      {inventory.product?.type || 'Product'}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Unit</p>
                    <p className="text-base text-gray-900">
                      {inventory.product?.unit?.name || 'N/A'} ({inventory.product?.unit?.abbreviation || 'N/A'})
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Status</p>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-1 ${inventory.product?.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                      }`}>
                      {inventory.product?.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Timestamps */}
            <div>
              <h5 className="text-lg font-medium text-gray-900 mb-3">Timestamps</h5>
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <p className="text-sm font-medium text-gray-500">Created</p>
                    <p className="text-base text-gray-900">{formatDate(inventory.createdAt)}</p>
                    {inventory.createdBy && (
                      <p className="text-xs text-gray-500">
                        by {inventory.createdBy.username || 'Unknown User'}
                      </p>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-500">Last Updated</p>
                    <p className="text-base text-gray-900">{formatDate(inventory.updatedAt)}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewProductModal